import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useMutation, useQuery} from '@apollo/client';
import {useNavigate} from 'react-router-dom';
import {useOrg} from '../contexts/OrgContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {useAuth} from '../contexts/AuthContext';
import {auth} from '../config/firebase';
import {
  GET_JOB,
  GET_WEB_CRAWL,
  GET_WEB_CRAWL_PAGES,
} from '../graphql/queries';
import {
  ADD_SOURCE_ITEMS_TO_GROUP,
  CREATE_DOCUMENT_GROUP,
  CREATE_ORGANIZATION_BRAND_PROFILE,
  GENERATE_GUIDE_FROM_GROUP,
  SET_GUIDE_THEME,
  START_GUIDE_GENERATION,
  START_WEB_CRAWL,
} from '../graphql/mutations';
import BrandingReviewCard, {BrandingSelection} from '../components/branding/BrandingReviewCard';

type BrandDecision = 'USE_AS_IS' | 'EDIT' | 'SKIP';

const APPROVAL_CONFIDENCE_THRESHOLD = 0.65;

export default function GuideImportFromUrlPage() {
  useDocumentTitle('Guides', 'Import from URL');
  const navigate = useNavigate();
  const {activeOrg} = useOrg();
  const {user} = useAuth();

  const [sourceUrl, setSourceUrl] = useState('');
  const [crawlJobId, setCrawlJobId] = useState('');
  const [crawlId, setCrawlId] = useState('');
  const [guideJobId, setGuideJobId] = useState('');
  const [decision, setDecision] = useState<BrandDecision>('USE_AS_IS');
  const [profileName, setProfileName] = useState('');
  const [approveForGuideUse, setApproveForGuideUse] = useState(false);
  const [selection, setSelection] = useState<BrandingSelection>({
    organizationDisplayName: '',
    logoUrl: '',
    headerLogoUrl: '',
    primaryColor: '#2563eb',
    secondaryColor: '#dbeafe',
    tone: 'NEUTRAL',
  });
  const [skipExistingUrls, setSkipExistingUrls] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [working, setWorking] = useState(false);

  const finalizedGuideRef = useRef<string | null>(null);

  const [startWebCrawl, {loading: startingCrawl}] = useMutation(START_WEB_CRAWL);
  const [createDocumentGroup] = useMutation(CREATE_DOCUMENT_GROUP);
  const [addSourceItemsToGroup] = useMutation(ADD_SOURCE_ITEMS_TO_GROUP);
  const [generateGuideFromGroup] = useMutation(GENERATE_GUIDE_FROM_GROUP);
  const [startGuideGeneration] = useMutation(START_GUIDE_GENERATION);
  const [createBrandProfile] = useMutation(CREATE_ORGANIZATION_BRAND_PROFILE);
  const [setGuideTheme] = useMutation(SET_GUIDE_THEME);

  const getAuthContext = useCallback(async () => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('Authentication token is unavailable. Please sign in again.');
    }
    return {
      headers: {
        authorization: `Bearer ${token}`,
      },
    };
  }, []);

  const {data: crawlJobData} = useQuery(GET_JOB, {
    variables: {id: crawlJobId},
    skip: !crawlJobId || !user,
    pollInterval: crawlJobId ? 3000 : 0,
    fetchPolicy: 'network-only',
  });
  const crawlJob = crawlJobData?.job;

  const {data: crawlData} = useQuery(GET_WEB_CRAWL, {
    variables: {id: crawlId},
    skip: !crawlId || !user,
    fetchPolicy: 'network-only',
  });
  const crawl = crawlData?.webCrawl;
  const discovery = crawl?.brandExtraction;

  const {data: crawlPagesData} = useQuery(GET_WEB_CRAWL_PAGES, {
    variables: {crawlId},
    skip: !crawlId || !user,
    fetchPolicy: 'network-only',
  });
  const sourceItemIds = useMemo(() => {
    const pages = crawlPagesData?.webCrawlPages || [];
    const ids = new Set<string>();
    pages.forEach((page: any) => {
      (page.documents || []).forEach((doc: any) => {
        if (doc.sourceItemId) ids.add(doc.sourceItemId);
      });
    });
    return Array.from(ids);
  }, [crawlPagesData]);

  const {data: guideJobData} = useQuery(GET_JOB, {
    variables: {id: guideJobId},
    skip: !guideJobId || !user,
    pollInterval: guideJobId ? 3000 : 0,
    fetchPolicy: 'network-only',
  });
  const guideJob = guideJobData?.job;

  useEffect(() => {
    if (!crawlJob || crawlId) return;
    if (crawlJob.status === 'FAILED') {
      setErrorMessage(crawlJob.errorMessage || 'URL crawl failed.');
      return;
    }
    if (crawlJob.status !== 'SUCCEEDED') return;

    const resultData = crawlJob.resultData || {};
    const nextCrawlId = resultData.crawlId || '';
    if (!nextCrawlId) {
      setErrorMessage('Crawl completed but no crawl result ID was returned.');
      return;
    }
    setCrawlId(nextCrawlId);
  }, [crawlId, crawlJob]);

  useEffect(() => {
    if (!discovery?.selectedCandidates) return;
    setSelection({
      organizationDisplayName: discovery.selectedCandidates.organizationDisplayName || activeOrg?.name || '',
      logoUrl: discovery.selectedCandidates.logoUrl || '',
      headerLogoUrl: discovery.selectedCandidates.headerLogoUrl || '',
      primaryColor: discovery.selectedCandidates.primaryColor || '#2563eb',
      secondaryColor: discovery.selectedCandidates.secondaryColor || '#dbeafe',
      tone: discovery.selectedCandidates.tone || 'NEUTRAL',
    });
    setProfileName(
      `${discovery.selectedCandidates.organizationDisplayName || activeOrg?.name || 'Imported'} URL profile`
    );
    setApproveForGuideUse((discovery.confidence || 0) >= APPROVAL_CONFIDENCE_THRESHOLD);
  }, [activeOrg?.name, discovery]);

  const finalizeGuide = useCallback(async (guideId: string) => {
    if (finalizedGuideRef.current === guideId) return;
    finalizedGuideRef.current = guideId;

    if (decision === 'SKIP') {
      navigate(`/org/guides/${guideId}/edit`);
      return;
    }
    if (!approveForGuideUse) {
      setErrorMessage('Approve the reviewed brand before applying it to the guide.');
      finalizedGuideRef.current = null;
      return;
    }

    const safeProfileName = profileName.trim() || 'URL imported profile';
    const provenance = [
      'source:url-import-flow',
      `source:${sourceUrl.trim()}`,
      crawlId ? `crawl:${crawlId}` : 'crawl:unknown',
      'reviewed:manual',
    ];

    try {
      const createResult = await createBrandProfile({
        variables: {
          input: {
            orgId: activeOrg?.id,
            name: safeProfileName,
            status: 'APPROVED',
            setAsDefault: false,
            organizationDisplayName: selection.organizationDisplayName || null,
            logoUrl: selection.logoUrl || null,
            headerLogoUrl: selection.headerLogoUrl || null,
            palette: {
              primary: selection.primaryColor || '#2563eb',
              secondary: selection.secondaryColor || '#dbeafe',
              background: '#f9fafb',
              surface: '#ffffff',
              text: '#111827',
            },
            tone: selection.tone || 'NEUTRAL',
            confidenceScore: discovery?.confidence,
            createdFromImportId: crawlId || sourceUrl.trim(),
            provenance,
          },
        },
        context: await getAuthContext(),
      });
      const profileId = createResult.data?.createOrganizationBrandProfile?.id;
      if (!profileId) {
        throw new Error('Profile created without ID');
      }

      await setGuideTheme({
        variables: {
          guideId,
          input: {
            brandMode: 'IMPORT_PROFILE',
            brandProfileId: profileId,
          },
        },
        context: await getAuthContext(),
      });

      navigate(`/org/guides/${guideId}/edit`);
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to apply reviewed branding to guide.');
      finalizedGuideRef.current = null;
    }
  }, [
    activeOrg?.id,
    approveForGuideUse,
    crawlId,
    createBrandProfile,
    decision,
    discovery?.confidence,
    getAuthContext,
    navigate,
    profileName,
    selection.logoUrl,
    selection.headerLogoUrl,
    selection.organizationDisplayName,
    selection.primaryColor,
    selection.secondaryColor,
    selection.tone,
    setGuideTheme,
    sourceUrl,
  ]);

  useEffect(() => {
    if (!guideJob) return;
    if (guideJob.status === 'FAILED') {
      setWorking(false);
      setErrorMessage(guideJob.errorMessage || 'Guide generation failed.');
      return;
    }
    if (guideJob.status !== 'SUCCEEDED') return;
    const nextGuideId = guideJob.resultRef || '';
    if (!nextGuideId) {
      setWorking(false);
      setErrorMessage('Guide generation completed but no guide ID was returned.');
      return;
    }
    setWorking(false);
    void finalizeGuide(nextGuideId);
  }, [finalizeGuide, guideJob]);

  const startImport = async () => {
    setErrorMessage('');
    finalizedGuideRef.current = null;
    const trimmed = sourceUrl.trim();
    if (!trimmed) {
      setErrorMessage('Enter a source URL to import.');
      return;
    }
    if (!activeOrg?.id) {
      setErrorMessage('Select an organization before importing from URL.');
      return;
    }
    try {
      const parsed = new URL(trimmed);
      const result = await startWebCrawl({
        variables: {
          orgId: activeOrg.id,
          seedUrl: parsed.href,
          maxDepth: 3,
          maxPages: 500,
          skipExistingUrls,
        },
        context: await getAuthContext(),
      });
      const nextJobId = result.data?.startWebCrawl?.jobId;
      if (!nextJobId) {
        throw new Error('No crawl job ID returned.');
      }
      setCrawlJobId(nextJobId);
      setCrawlId('');
      setGuideJobId('');
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to start URL import crawl.');
    }
  };

  const generateGuide = async () => {
    if (!activeOrg?.id || !crawl || !crawlId) {
      setErrorMessage('Crawl results are not ready yet.');
      return;
    }
    if (decision !== 'SKIP' && !approveForGuideUse) {
      setErrorMessage('Approve reviewed branding before generating an applied branded guide.');
      return;
    }

    setErrorMessage('');
    setWorking(true);
    finalizedGuideRef.current = null;
    try {
      if (sourceItemIds.length === 0) {
        // Fallback path: some URLs have no downloadable docs. We still generate
        // a first draft guide from URL context so the "one flow" experience
        // completes in a single run.
        const orgSlug = (activeOrg.slug || '').trim();
        if (!orgSlug) {
          throw new Error('Active organization slug is missing for fallback generation.');
        }
        const generationPrompt = `Create a practical public guide for ${activeOrg.name} using process information from ${crawl.seedUrl}. Include clear steps, key dates, and FAQs.`;
        const startResult = await startGuideGeneration({
          variables: {
            prompt: generationPrompt,
            orgSlug,
          },
          context: await getAuthContext(),
        });
        const fallbackJobId = startResult.data?.startGuideGeneration?.jobId;
        if (!fallbackJobId) {
          const graphqlErrors = (startResult as any)?.errors
            ?.map((error: {message?: string}) => error.message)
            .filter(Boolean)
            .join('; ');
          throw new Error(graphqlErrors || 'Failed to start guide job.');
        }
        setGuideJobId(fallbackJobId);
        return;
      }

      const host = (() => {
        try {
          return new URL(crawl.seedUrl).hostname.replace(/^www\./, '');
        } catch {
          return 'url-import';
        }
      })();
      const groupName = `${host} import ${new Date().toISOString().slice(0, 10)}`;
      const createGroupResult = await createDocumentGroup({
        variables: {
          orgId: activeOrg.id,
          name: groupName,
          description: `Auto-created from crawl ${crawl.id} (${crawl.seedUrl})`,
          groupType: 'JOURNEY',
        },
        context: await getAuthContext(),
      });
      const groupId = createGroupResult.data?.createDocumentGroup?.id;
      if (!groupId) {
        throw new Error('Failed to create import document group.');
      }

      await addSourceItemsToGroup({
        variables: {
          groupId,
          sourceItemIds,
          assignedBy: 'admin',
        },
        context: await getAuthContext(),
      });

      const generateResult = await generateGuideFromGroup({
        variables: {groupId},
        context: await getAuthContext(),
      });
      const generation = generateResult.data?.generateGuideFromGroup;
      if (!generation) {
        throw new Error('Guide generation did not return a result.');
      }
      if (generation.existingGuide && generation.guide?.id) {
        setWorking(false);
        await finalizeGuide(generation.guide.id);
        return;
      }
      if (!generation.generationJobId) {
        throw new Error('No job ID returned.');
      }
      setGuideJobId(generation.generationJobId);
    } catch (error: any) {
      setWorking(false);
      setErrorMessage(error.message || 'Failed to generate guide from crawl.');
    }
  };

  const crawlJobStatus = crawlJob?.status;
  const guideJobStatus = guideJob?.status;
  const crawlReady = !!crawlId && !!crawl;
  const isCrawling = !!crawlJobId && crawlJobStatus !== 'SUCCEEDED' && crawlJobStatus !== 'FAILED';
  const isGenerating = !!guideJobId && guideJobStatus !== 'SUCCEEDED' && guideJobStatus !== 'FAILED';

  return (
    <Box sx={{maxWidth: 1040, margin: '0 auto', padding: '24px 24px 64px'}}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/org/guides')}
        sx={{mb: 2, textTransform: 'none', color: '#6B7280'}}
      >
        Back to guides
      </Button>

      <Typography sx={{fontSize: 24, fontWeight: 650}}>Import guide from URL</Typography>
      <Typography sx={{fontSize: 14, color: 'text.secondary', mt: 0.5, mb: 2.5}}>
        One flow: crawl URL → review branding → generate guide → apply reviewed brand.
      </Typography>

      <Box sx={{bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.5, mb: 2}}>
        <Stack spacing={1.5}>
          <TextField
            label="Source URL"
            value={sourceUrl}
            onChange={event => setSourceUrl(event.target.value)}
            placeholder="https://planapus.com"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={skipExistingUrls}
                onChange={e => setSkipExistingUrls(e.target.checked)}
                disabled={startingCrawl || isCrawling}
              />
            }
            label={<Typography sx={{fontSize: 13, color: '#6B7280'}}>Skip documents already in library</Typography>}
          />
          <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
            <Button
              variant="contained"
              onClick={startImport}
              disabled={startingCrawl || isCrawling}
            >
              {(startingCrawl || isCrawling) ? <CircularProgress size={16} sx={{mr: 1}} /> : null}
              Start crawl
            </Button>
            {crawlJobStatus && (
              <Typography sx={{fontSize: 13, color: '#6B7280'}}>
                Crawl status: {crawlJobStatus}
              </Typography>
            )}
          </Box>
          {crawlReady && (
            <Alert severity="success">
              Crawl completed with {sourceItemIds.length} imported source document(s). Continue with brand review.
            </Alert>
          )}
        </Stack>
      </Box>

      {crawlReady && discovery && (
        <Box sx={{mb: 2}}>
          <BrandingReviewCard
            title="Branding review"
            subtitle="Review before applying. Discovery never auto-applies."
            confidence={discovery.confidence}
            warnings={discovery.warnings || []}
            selection={selection}
            editable={decision === 'EDIT'}
            onChange={setSelection}
            actions={(
              <Stack spacing={1.25}>
                <TextField
                  select
                  label="Brand decision"
                  value={decision}
                  onChange={event => setDecision(event.target.value as BrandDecision)}
                >
                  <MenuItem value="USE_AS_IS">Use as-is</MenuItem>
                  <MenuItem value="EDIT">Edit before applying</MenuItem>
                  <MenuItem value="SKIP">Skip branding</MenuItem>
                </TextField>
                {decision !== 'SKIP' && (
                  <>
                    <TextField
                      label="Profile name"
                      value={profileName}
                      onChange={event => setProfileName(event.target.value)}
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={approveForGuideUse}
                          onChange={event => setApproveForGuideUse(event.target.checked)}
                        />
                      )}
                      label="I reviewed and approve this branding for guide usage"
                    />
                  </>
                )}
              </Stack>
            )}
          />
        </Box>
      )}

      {crawlReady && (
        <Box sx={{bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.5}}>
          <Stack spacing={1.25}>
            <Typography sx={{fontSize: 14, fontWeight: 600}}>
              Generate guide
            </Typography>
            <Typography sx={{fontSize: 13, color: '#6B7280'}}>
              This will create a journey document group from crawl imports and generate a guide.
            </Typography>
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
              <Button
                variant="contained"
                onClick={generateGuide}
                disabled={working || isGenerating}
              >
                {(working || isGenerating) ? <CircularProgress size={16} sx={{mr: 1}} /> : null}
                Generate guide from crawl
              </Button>
              {guideJobStatus && (
                <Typography sx={{fontSize: 13, color: '#6B7280'}}>
                  Guide generation status: {guideJobStatus}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{mt: 2}}>
          {errorMessage}
        </Alert>
      )}
    </Box>
  );
}
